import { firebaseApp } from './firebase'

// Subida de imágenes al bucket de medios (bee-camp-fb-media) para el message board.
// Firebase Storage SDK se importa DINÁMICO: no debe entrar al chunk de arranque
// (solo el editor de posts lo necesita). Devuelve una URL de descarga con token
// (pública-por-token) que se embebe como <img src> en el JSON tiptap del post.
// Reglas: solo cuentas de bee (@beemexico.com o @gelimer.mx), imágenes < 5 MB, ruta
// posts/<pid>/... (storage/storage.rules).

const MAX_BYTES = 5 * 1024 * 1024
const MAX_DIM = 1600 // lado mayor tras redimensionar (px)
const WEBP_QUALITY = 0.82

// Id corto único para el nombre del archivo (tiempo base36 + aleatorio).
const uid = (): string =>
  `img_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`

export function isImageFile(f: File): boolean {
  return f.type.startsWith('image/')
}

// Comprime a WebP redimensionando el lado mayor a MAX_DIM. Los GIF se dejan intactos
// (convertirlos a WebP por canvas los aplana a un solo cuadro y pierden la animación).
// Si el navegador no puede codificar WebP o algo falla, cae al archivo original.
async function toWebp(file: File): Promise<{ blob: Blob; ext: string; type: string }> {
  if (file.type === 'image/gif') return { blob: file, ext: 'gif', type: 'image/gif' }
  try {
    // createImageBitmap respeta la orientación EXIF (from-image); fallback a <img>.
    let bitmap: ImageBitmap
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions)
    } catch {
      bitmap = await createImageBitmap(file)
    }
    const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height))
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('sin contexto 2d')
    ctx.drawImage(bitmap, 0, 0, w, h)
    bitmap.close?.()
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, 'image/webp', WEBP_QUALITY),
    )
    if (!blob) throw new Error('toBlob nulo')
    // Si por alguna razón el WebP pesa más que el original, usa el original.
    if (blob.size >= file.size && file.size <= MAX_BYTES) {
      return { blob: file, ext: (file.name.split('.').pop() || 'png').toLowerCase(), type: file.type }
    }
    return { blob, ext: 'webp', type: 'image/webp' }
  } catch {
    return { blob: file, ext: (file.name.split('.').pop() || 'png').toLowerCase(), type: file.type }
  }
}

// Subida común: valida que sea imagen, comprime a WebP y sube bajo la carpeta
// indicada. camp NO acepta otros formatos a propósito (ni PDF ni documentos):
// las reglas de Storage también lo exigen, así que el límite está en los dos lados.
async function subirImagen(carpeta: string, file: File): Promise<string> {
  if (!isImageFile(file)) throw new Error('Solo se pueden subir imágenes')
  if (file.size > MAX_BYTES) throw new Error('La imagen supera 5 MB')
  const { blob, ext, type } = await toWebp(file)
  if (blob.size > MAX_BYTES) throw new Error('La imagen supera 5 MB')
  const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage')
  const safeExt = ext.replace(/[^a-z0-9]/g, '') || 'webp'
  const storage = getStorage(firebaseApp)
  const r = ref(storage, `${carpeta}/${uid()}.${safeExt}`)
  await uploadBytes(r, blob, { contentType: type })
  return getDownloadURL(r)
}

export const uploadPostImage = (pid: string, file: File) => subirImagen(`posts/${pid}`, file)
export const uploadChatImage = (chatId: string, file: File) => subirImagen(`chat/${chatId}`, file)
